import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { DEFAULT_TIMEZONE } from '../shared/dates'
import { getInvoiceDates, getServiceDateTextFormat } from '../shared/invoices'
import type {
  ClientInvoicesByPeriodSearchOptions,
  InvoiceClientDto,
  LocationPetProfileClientDto,
  OrderClientDto,
} from '../types/api'
import { SubStatus, TCustomInvoiceDto } from '../types/general'

const TIME_FORMAT = 'h:mm aaa'

const mapBaseInvoice = (invoice: InvoiceClientDto, orderId?: string): TCustomInvoiceDto => {
  const { startDate, endDate, hasStartTime, hasEndTime } = getInvoiceDates(invoice)
  const serviceName = getServiceName(invoice)
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE

  return {
    id: invoice.id,
    purchaseDate: formatInvoiceDate(invoice.updatedAt),
    service: serviceName,
    serviceTypeName: invoice.locationServiceType?.serviceType?.name ?? null,
    invoiceStatus: invoice.invoiceStatus,
    invoiceSubStatus: invoice.invoiceSubStatus,
    serviceDate: getServiceDateTextFormat(startDate, endDate, serviceName, 1, { fullYearLast: true }),
    startDate: startDate ? startDate.toISOString() : undefined,
    endDate: endDate ? endDate.toISOString() : undefined,
    startTime: hasStartTime && startDate ? formatInTimeZone(startDate, timezone, TIME_FORMAT) : undefined,
    endTime: hasEndTime && endDate ? formatInTimeZone(endDate, timezone, TIME_FORMAT) : undefined,
    orderId: orderId ?? invoice.order?.id,
    pets: getInvoicePets(invoice),
  }
}

export const formatInvoiceDate = (date?: string | number) => format(new Date(date ?? 0), 'MMM d, yyyy')

export const getServiceName = (invoice: InvoiceClientDto) =>
  invoice.locationServiceType?.displayName || invoice.locationServiceType?.serviceType?.displayName

const getInvoicePets = (invoice: InvoiceClientDto): LocationPetProfileClientDto[] => {
  const pets = (invoice.items ?? [])
    .flatMap((item) => item.petRelations ?? [])
    .map((relation) => relation.invoicePet?.locationPetProfile)
    .filter((lpp): lpp is LocationPetProfileClientDto => !!lpp)

  return [...new Map(pets.map((pet) => [pet.id, pet])).values()]
}

export const getCustomInvoicePetNames = (invoice: TCustomInvoiceDto): string =>
  (invoice.pets ?? [])
    .map((pet) => pet.displayName)
    .filter(Boolean)
    .join(', ')

export const mapInvoiceToCustomInvoices = (invoice: InvoiceClientDto): TCustomInvoiceDto[] => [mapBaseInvoice(invoice)]

export const mapOrderToCustomInvoices = (orders: OrderClientDto[]): TCustomInvoiceDto[] =>
  orders.flatMap((order) => order.invoices?.map((invoice) => mapBaseInvoice(invoice, order.id)) ?? [])

export const getServiceOptions = (
  options: ClientInvoicesByPeriodSearchOptions
): ClientInvoicesByPeriodSearchOptions => ({
  ...options,
  includeRequested: !options.invoiceSubStatus?.length || options.invoiceSubStatus.includes(SubStatus.REQUESTED),
})
